'use client'

import {useCallback, useEffect, useRef, useState} from 'react'

import Modal from '@/components/Modal/Modal'
import getHistoryOrder from '@/services/OrderService/getHistoryOrder'
import {getErrorMessage} from '@/utils/errorMessages'
import type {historyLineItem} from '@/types/order'
import HistoryTimeline from './HistoryTimeline'

interface OrderHistoryDialogProps {
    isOpen: boolean
    orderId: string
    orderNumber: string
    onClose: () => void
}

const OrderHistoryDialog = ({
    isOpen,
    orderId,
    orderNumber,
    onClose,
}: OrderHistoryDialogProps) => {
    const [history, setHistory] = useState<historyLineItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const requestIdRef = useRef(0)

    const loadHistory = useCallback(async () => {
        const requestId = ++requestIdRef.current
        setIsLoading(true)
        setErrorMessage('')

        try {
            const result = await getHistoryOrder(orderId)
            if (requestId !== requestIdRef.current) return
            setHistory(result)
        } catch (error) {
            if (requestId !== requestIdRef.current) return
            setErrorMessage(getErrorMessage(error, 'Unable to load order history.'))
        }

        setIsLoading(false)
    }, [orderId])

    useEffect(() => {
        setHistory([])
        setErrorMessage('')
    }, [orderId])

    useEffect(() => {
        if (isOpen) {
            void loadHistory()
            return
        }

        requestIdRef.current += 1
    }, [isOpen, loadHistory])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Order History"
            className="!w-[640px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-300px)] max-md:max-h-[calc(100vh-160px)] overflow-y-auto overscroll-contain !p-0"
            containerClassName="!overflow-hidden max-md:!py-[32px]"
        >
            <section className="p-xl">
                <HistoryTimeline
                    title={`Order #${orderNumber}`}
                    history={history}
                    isLoading={isLoading}
                    hasError={Boolean(errorMessage)}
                    onRefresh={loadHistory}
                    emptyMessage="No changes have been recorded for this order yet."
                    errorMessage={errorMessage}
                />
            </section>
        </Modal>
    )
}

export default OrderHistoryDialog
